import { Upload } from "antd-v5";

import InputLabel from "base/basicComponents/inputLabel";
import NormalModal from "base/basicComponents/modal";
import NormalSelect from "base/basicComponents/select";
import FileItem from "base/basicComponents/uploadFile/fileItem";
import uploadFileIcon from "assets/image/uploadFileicon.png";
import tipIcon from "assets/image/tipIcon.png";

import styles from "./uploadModal.module.scss";
import { fileUploadErrorMessage } from "app/publicDictionaryValues";
import {
  ACCEPTED_FILE_TYPES,
  INVALID_FILE_FORMAT_MESSAGE,
  METRIC_OPTIONS,
  NO_FILE_SELECTED_MESSAGE,
} from "./constants";
import { getFileExtension, isMetricOne } from "./utils";
import useMonitoringReportUpload from "./hooks/useMonitoringReportUpload/useMonitoringReportUpload";
import Loading from "../loading/index";
import ResponseMsg from "../responseMsg";

interface MonitoringReportUploadModalProps {
  visible: boolean;
  onToggle: (flag: boolean) => void;
}

const MonitoringReportUploadModal = ({ visible, onToggle }: MonitoringReportUploadModalProps) => {
  const {
    metric,
    quarterPeriod,
    monthPeriod,
    selectedFile,
    uploadFileList,
    fieldErrors,
    quarterYearOptions,
    monthYearOptions,
    availableQuarterOptions,
    availableMonthOptions,
    handleMetricChange,
    handleQuarterYearChange,
    handleQuarterChange,
    handleMonthYearChange,
    handleMonthChange,
    handleRemoveSelectedFile,
    handleBeforeUpload,
    handleUploadChange,
    result,
    loading,
    handleSubmit,
    reset,
  } = useMonitoringReportUpload();
  const metricOneSelected = isMetricOne(metric);

  return (
    <NormalModal
      title={result.uploaded ? "Upload Result" : "Upload File"}
      okText="Upload"
      cancelText="Cancel"
      isModalOpen={visible}
      handleOk={handleSubmit}
      handleCancel={() => {
        reset();
        onToggle(false);
      }}
      closable={true}
      width={650}
      centered={true}
      footer={result.uploaded ? null : undefined}
      testid="monitoring-report-upload-modal"
      className={styles.modalContainer}
      childContent={
        loading ? (
          <Loading />
        ) : result.response && result.uploaded ? (
          <ResponseMsg response={result.response} />
        ) : (
          <div className={styles.formContent}>
            <div className={styles.singleFieldRow}>
              <InputLabel text="Metric" required className={styles.fieldLabel} />
              <div className={styles.fieldGroup}>
                <NormalSelect
                  optionList={METRIC_OPTIONS}
                  value={metric}
                  handleChange={handleMetricChange}
                  className={styles.selectField}
                  testid="monitoring-report-metric-select"
                />
              </div>
            </div>

            <div className={styles.doubleFieldRow}>
              <InputLabel text="Auditing Quarter" required className={styles.fieldLabel} />
              <div className={styles.fieldGroup}>
                <div className={styles.doubleFieldControls}>
                  <NormalSelect
                    optionList={quarterYearOptions}
                    value={quarterPeriod.year}
                    handleChange={handleQuarterYearChange}
                    disabled={metricOneSelected}
                    className={styles.selectField}
                    testid="monitoring-report-quarter-year-select"
                  />
                  <NormalSelect
                    optionList={availableQuarterOptions}
                    value={quarterPeriod.quarter}
                    handleChange={handleQuarterChange}
                    disabled={metricOneSelected}
                    status={fieldErrors.quarterNotSelected ? "error" : ""}
                    className={styles.selectField}
                    testid="monitoring-report-quarter-select"
                  />
                </div>
              </div>
            </div>

            <div className={styles.doubleFieldRow}>
              <InputLabel text="Auditing Month" required className={styles.fieldLabel} />
              <div className={styles.fieldGroup}>
                <div className={styles.doubleFieldControls}>
                  <NormalSelect
                    optionList={monthYearOptions}
                    value={monthPeriod.year}
                    handleChange={handleMonthYearChange}
                    disabled={!metricOneSelected}
                    className={styles.selectField}
                    testid="monitoring-report-month-year-select"
                  />
                  <NormalSelect
                    optionList={availableMonthOptions}
                    value={monthPeriod.month}
                    handleChange={handleMonthChange}
                    disabled={!metricOneSelected}
                    status={fieldErrors.monthNotSelected ? "error" : ""}
                    className={styles.selectField}
                    testid="monitoring-report-month-select"
                  />
                </div>
              </div>
            </div>

            <div className={styles.fileSection}>
              <InputLabel text="Select File" required className={styles.fieldLabel} />
              <div className={styles.fileTrigger} data-testid="monitoring-report-file-trigger">
                <Upload
                  fileList={uploadFileList}
                  beforeUpload={handleBeforeUpload}
                  onChange={handleUploadChange}
                  showUploadList={false}
                  maxCount={1}
                  multiple={false}
                  accept={ACCEPTED_FILE_TYPES}
                >
                  <div className={styles.fileTriggerContent}>
                    <img
                      src={uploadFileIcon}
                      alt="Upload file"
                      className={styles.fileTriggerIcon}
                    />
                    <div className={styles.fileTriggerText}>Drag and Drop file here</div>
                    <div className={styles.fileTriggerHint}>
                      <img src={tipIcon} alt="" className={styles.fileTriggerHintIcon} />
                      <span>
                        You can only upload one file as attachment. If you upload again, the old one
                        will be replaced.
                      </span>
                    </div>
                  </div>
                </Upload>
              </div>
              {selectedFile && (
                <FileItem
                  fileName={selectedFile.name}
                  fileType={getFileExtension(selectedFile.name)}
                  index={0}
                  isShowDeleteBtn={true}
                  deleteFile={handleRemoveSelectedFile}
                  fileNameType="text"
                  isDownload={false}
                  className={styles.selectedFileItem}
                />
              )}
              {(fieldErrors.fileNotChosen ||
                fieldErrors.fileFormatInvalid ||
                fieldErrors.fileTooLarge) && (
                <div className={styles.fileTriggerError} data-testid="monitoring-report-file-name">
                  {fieldErrors.fileNotChosen && NO_FILE_SELECTED_MESSAGE}
                  {fieldErrors.fileFormatInvalid && INVALID_FILE_FORMAT_MESSAGE}
                  {fieldErrors.fileTooLarge && fileUploadErrorMessage.single}
                </div>
              )}
            </div>
          </div>
        )
      }
    />
  );
};

export default MonitoringReportUploadModal;
